import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Empty, ErrorBox, Ltr, Row, Spinner, Toggle } from '../components/ui.js';
import { TeamsTab } from '../components/AdminTeams.js';
import { BroadcastTab } from '../components/AdminBroadcast.js';

type Tab = 'users' | 'teams' | 'broadcast' | 'features' | 'audit' | 'health' | 'policy';

const TABS: Tab[] = ['users', 'teams', 'broadcast', 'features', 'policy', 'audit', 'health'];

const TAB_LABELS: Partial<Record<Tab, string>> = {
  users: 'admin.users',
  teams: 'admin.teams',
  broadcast: 'admin.broadcast',
  features: 'admin.features',
  audit: 'admin.audit',
  health: 'admin.health',
};

export function AdminPage(): JSX.Element {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('users');

  // The broadcast console reaches people outside this workspace, so it is a
  // SUPER_ADMIN tool; a tenant admin should not be shown a tab that 403s.
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api<{ user: { botRole: string } }>('/me'),
  });

  const overview = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () =>
      api<{
        tenant: { siteName: string; provider: string; policy: Record<string, unknown> | null } | null;
        counts: { members: number; connected: number };
        deliveries: { byStatus: Record<string, number>; bySuppression: Record<string, number> };
        webhooks: { registrations: unknown[]; backlog: number; deadLetters: number };
        note: string;
      }>('/admin/overview'),
  });

  if (overview.isLoading) return <Spinner />;
  if (overview.error) {
    return (
      <div className="page">
        <ErrorBox error={overview.error} />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="header">
        <h1>{t('admin.title').replace(/<[^>]+>/g, '')}</h1>
        <span className="muted small">{overview.data?.tenant?.siteName}</span>
      </div>

      <div className="note small">{overview.data?.note}</div>

      <div className="grid">
        <div className="tile">
          <div className="tile-value">{overview.data?.counts.members ?? 0}</div>
          <div className="tile-label">{t('admin.users')}</div>
        </div>
        <div className="tile">
          <div className="tile-value">{overview.data?.counts.connected ?? 0}</div>
          <div className="tile-label">connected</div>
        </div>
      </div>

      <div className="scroll-x">
        {TABS.filter(
          (item) => item !== 'broadcast' || me.data?.user.botRole === 'SUPER_ADMIN',
        ).map((item) => (
          <button
            key={item}
            type="button"
            className={`chip ${tab === item ? 'chip-indeterminate' : ''}`}
            onClick={() => setTab(item)}
          >
            {TAB_LABELS[item] ? t(TAB_LABELS[item]) : item}
          </button>
        ))}
      </div>

      {tab === 'users' ? <UsersTab /> : null}
      {tab === 'teams' ? <TeamsTab /> : null}
      {tab === 'broadcast' ? <BroadcastTab /> : null}
      {tab === 'features' ? <FeaturesTab /> : null}
      {tab === 'policy' ? <PolicyTab /> : null}
      {tab === 'audit' ? <AuditTab /> : null}
      {tab === 'health' ? <HealthTab /> : null}
    </div>
  );
}

function UsersTab(): JSX.Element {
  const { t } = useI18n();
  const client = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ['admin-users'],
    queryFn: () =>
      api<{
        total: number;
        users: Array<{
          telegramUserId: string;
          telegramId: string;
          username: string | null;
          displayName: string | null;
          botRole: string;
          active: boolean;
          jira:
            | { linked: true; displayName: string | null; tokenStatus: string; grantedScopeCount: number; lastErrorCode: string | null }
            | { linked: false };
        }>;
      }>('/admin/users'),
  });

  const setRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      api(`/admin/users/${userId}/role`, { method: 'PUT', body: { role } }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const setActive = useMutation({
    mutationFn: ({ userId, active }: { userId: string; active: boolean }) =>
      api(`/admin/users/${userId}/active`, { method: 'PUT', body: { active } }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  if (users.isLoading) return <Spinner />;
  if (users.error) return <ErrorBox error={users.error} />;

  return (
    <>
      <Card title={`${t('admin.users')} (${users.data?.total ?? 0})`}>
        {(users.data?.users ?? []).map((user) => (
          <div key={user.telegramUserId} className="row" style={{ display: 'block' }}>
            <div className="row-title">
              {user.displayName ?? user.username ?? <Ltr>{user.telegramId}</Ltr>}
            </div>
            <div className="row-sub">
              <span className="chip">{user.botRole}</span>{' '}
              {user.jira.linked ? (
                <>
                  <span
                    className={`chip ${user.jira.tokenStatus === 'healthy' ? 'chip-done' : 'chip-danger'}`}
                  >
                    {user.jira.tokenStatus}
                  </span>{' '}
                  <span className="muted small">
                    {user.jira.displayName} · {user.jira.grantedScopeCount} scopes
                  </span>
                </>
              ) : (
                <span className="chip">not linked</span>
              )}
            </div>
            <div className="btn-row" style={{ marginTop: 8 }}>
              <select
                value={user.botRole}
                onChange={(event) =>
                  setRole.mutate({ userId: user.telegramUserId, role: event.target.value })
                }
                style={{ width: 'auto' }}
              >
                <option value="USER">USER</option>
                <option value="AUDITOR">AUDITOR</option>
                <option value="TENANT_ADMIN">TENANT_ADMIN</option>
              </select>
              <button
                type="button"
                className={user.active ? 'btn btn-danger' : 'btn'}
                onClick={() =>
                  setActive.mutate({ userId: user.telegramUserId, active: !user.active })
                }
              >
                {user.active ? t('common.disabled') : t('common.enabled')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setSelected(user.telegramUserId)}
              >
                🔔
              </button>
            </div>
          </div>
        ))}
      </Card>

      {setRole.error ? <ErrorBox error={setRole.error} /> : null}
      {selected ? <UserNotifications userId={selected} /> : null}
      {selected ? <UserPermissions userId={selected} /> : null}
    </>
  );
}

function UserNotifications({ userId }: { userId: string }): JSX.Element {
  const { t } = useI18n();
  const client = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-user-notify', userId],
    queryFn: () =>
      api<{
        preference: {
          adminEnabled: boolean;
          adminLocked: boolean;
          lockedEvents: string[];
          eventSettings: Record<string, boolean>;
        };
      }>(`/admin/users/${userId}/notifications`),
  });

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/admin/users/${userId}/notifications`, { method: 'PATCH', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin-user-notify', userId] }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Empty />;

  return (
    <Card title={t('admin.notifications')}>
      <Toggle
        label="Notifications allowed"
        checked={data.preference.adminEnabled}
        onChange={(next) => patch.mutate({ adminEnabled: next })}
      />
      <Toggle
        label="Lock user settings"
        checked={data.preference.adminLocked}
        onChange={(next) => patch.mutate({ adminLocked: next })}
      />
      <div className="small muted" style={{ marginTop: 8 }}>
        {data.preference.lockedEvents.length} locked events
      </div>
      {patch.error ? <ErrorBox error={patch.error} /> : null}
    </Card>
  );
}

function UserPermissions({ userId }: { userId: string }): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-user-perms', userId],
    queryFn: () =>
      api<{
        snapshots: Array<{
          scope: string;
          scopeKey: string | null;
          capabilities: Record<string, unknown>;
          computedAt: string;
          stale: boolean;
        }>;
        note: string;
      }>(`/admin/users/${userId}/permissions`),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  return (
    <Card title="Permission snapshots">
      <div className="note small">{data?.note}</div>
      {(data?.snapshots ?? []).length === 0 ? <Empty /> : null}
      {(data?.snapshots ?? []).map((snapshot) => (
        <Row
          key={`${snapshot.scope}-${snapshot.scopeKey}`}
          title={`${snapshot.scope} ${snapshot.scopeKey ?? ''}`}
          subtitle={new Date(snapshot.computedAt).toLocaleString()}
          right={
            <span className={`chip ${snapshot.stale ? 'chip-danger' : 'chip-done'}`}>
              {snapshot.stale ? 'stale' : 'fresh'}
            </span>
          }
        />
      ))}
    </Card>
  );
}

function FeaturesTab(): JSX.Element {
  const client = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-features'],
    queryFn: () =>
      api<{
        modules: string[];
        groups: Record<string, string[]>;
        policies: Array<{ module: string; enabled: boolean; locked: boolean; projectKey: string }>;
      }>('/admin/features'),
  });

  const set = useMutation({
    mutationFn: ({ module, enabled, locked }: { module: string; enabled: boolean; locked?: boolean }) =>
      api(`/admin/features/${module}`, { method: 'PUT', body: { enabled, locked } }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin-features'] }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  const byModule = new Map(
    (data?.policies ?? []).filter((p) => p.projectKey === '').map((p) => [p.module, p]),
  );

  return (
    <>
      {Object.entries(data?.groups ?? {}).map(([group, modules]) => (
        <Card key={group} title={group}>
          {modules.map((module) => {
            const policy = byModule.get(module);
            return (
              <Toggle
                key={module}
                label={module}
                checked={policy?.enabled ?? true}
                hint={policy?.locked ? 'locked' : undefined}
                onChange={(next) => set.mutate({ module, enabled: next })}
              />
            );
          })}
        </Card>
      ))}
      {set.error ? <ErrorBox error={set.error} /> : null}
    </>
  );
}

function PolicyTab(): JSX.Element {
  const client = useQueryClient();
  const [jql, setJql] = useState('');

  const overview = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () =>
      api<{ tenant: { policy: { strictPersonalWorkspace: boolean; personalWorkspaceJql: string | null; notificationsEnabled: boolean } | null } | null }>(
        '/admin/overview',
      ),
  });

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api('/admin/tenant/policy', { method: 'PATCH', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin-overview'] }),
  });

  const policy = overview.data?.tenant?.policy;

  return (
    <Card title="Tenant policy">
      <Toggle
        label="STRICT_PERSONAL_WORKSPACE"
        checked={policy?.strictPersonalWorkspace ?? false}
        hint="Narrows listings to a personal-work JQL. Jira permissions still apply first."
        onChange={(next) => patch.mutate({ strictPersonalWorkspace: next })}
      />
      <div className="field">
        <label htmlFor="pwjql">Personal workspace JQL</label>
        <textarea
          id="pwjql"
          className="mono"
          defaultValue={policy?.personalWorkspaceJql ?? ''}
          onChange={(event) => setJql(event.target.value)}
        />
        <button
          type="button"
          className="btn"
          onClick={() => patch.mutate({ personalWorkspaceJql: jql || null })}
        >
          Save
        </button>
      </div>
      <Toggle
        label="Notifications enabled"
        checked={policy?.notificationsEnabled ?? true}
        onChange={(next) => patch.mutate({ notificationsEnabled: next })}
      />
      {patch.error ? <ErrorBox error={patch.error} /> : null}
    </Card>
  );
}

function AuditTab(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-audit'],
    queryFn: () =>
      api<{
        total: number;
        rows: Array<{
          id: string;
          category: string;
          action: string;
          targetKey: string | null;
          result: string;
          dangerLevel: string | null;
          createdAt: string;
          telegramUser: { username: string | null; firstName: string | null } | null;
        }>;
      }>('/admin/audit?take=50'),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  return (
    <Card title={`Audit (${data?.total ?? 0})`}>
      {(data?.rows ?? []).map((row) => (
        <Row
          key={row.id}
          title={`${row.category}.${row.action}${row.targetKey ? ` · ${row.targetKey}` : ''}`}
          subtitle={`${row.telegramUser?.firstName ?? row.telegramUser?.username ?? '—'} · ${new Date(row.createdAt).toLocaleString()}`}
          right={
            <span className={`chip ${row.result === 'SUCCESS' ? 'chip-done' : 'chip-danger'}`}>
              {row.dangerLevel ?? row.result}
            </span>
          }
        />
      ))}
    </Card>
  );
}

function HealthTab(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-health'],
    queryFn: () =>
      api<{
        webhooks: { registrations: Array<{ state: string; expiresAt: string }>; backlog: number; deadLetters: number; receivedLastHour: number };
        deliveries: { byStatus: Record<string, number>; bySuppression: Record<string, number> };
        deadLetters: number;
        queues: Record<string, number>;
        reauthRequired: number;
      }>('/admin/health'),
    refetchInterval: 30_000,
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  return (
    <>
      <Card title="Webhooks">
        <Row title="Registrations" right={data?.webhooks.registrations.length ?? 0} />
        <Row title="Received (1h)" right={data?.webhooks.receivedLastHour ?? 0} />
        <Row title="Backlog" right={data?.webhooks.backlog ?? 0} />
        <Row
          title="Dead letters"
          right={
            <span className={`chip ${(data?.deadLetters ?? 0) > 0 ? 'chip-danger' : 'chip-done'}`}>
              {data?.deadLetters ?? 0}
            </span>
          }
        />
      </Card>

      <Card title="Deliveries (24h)">
        {Object.entries(data?.deliveries.byStatus ?? {}).map(([status, count]) => (
          <Row key={status} title={status} right={count} />
        ))}
      </Card>

      <Card title="Suppressed">
        {Object.entries(data?.deliveries.bySuppression ?? {}).map(([reason, count]) => (
          <Row key={reason} title={reason} right={count} />
        ))}
      </Card>

      <Card title="Queues">
        {Object.entries(data?.queues ?? {}).map(([queue, depth]) => (
          <Row key={queue} title={queue} right={depth} />
        ))}
        <Row
          title="Needs reconnect"
          right={
            <span className={`chip ${(data?.reauthRequired ?? 0) > 0 ? 'chip-danger' : 'chip-done'}`}>
              {data?.reauthRequired ?? 0}
            </span>
          }
        />
      </Card>
    </>
  );
}
